import { useState } from 'react';
import { Clock, DollarSign, Activity, Database, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import ConfirmDialog from '@/components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransfersTab({ transferConfig, transfers, onReload, setToast }) {
  const [showTransferConfigModal, setShowTransferConfigModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const handleToggleTransfers = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/super-admin/transfer-config`, { transfers_enabled: enabled }, { headers: { Authorization: `Bearer ${token}` } });
      setToast({ type: 'success', message: `Transferencias ${enabled ? 'habilitadas' : 'deshabilitadas'}` });
      onReload();
    } catch (error) {
      setToast({ type: 'error', message: 'Error al cambiar estado de transferencias' });
    }
  };

  const handleExecuteTransfersNow = () => {
    setConfirmDialog({
      title: 'Ejecutar Transferencias Ahora?',
      message: 'Esto creara y procesara todas las transferencias pendientes inmediatamente. Esta accion no se puede deshacer.',
      type: 'warning',
      confirmText: 'Ejecutar Ahora',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${BACKEND_URL}/api/super-admin/transfers/execute-now`, null, { headers: { Authorization: `Bearer ${token}` } });
          setToast({ type: 'success', message: `Transferencias ejecutadas: ${response.data.completed} exitosas, ${response.data.failed} fallidas` });
          onReload();
        } catch (error) {
          setToast({ type: 'error', message: error.response?.data?.detail || 'Error al ejecutar transferencias' });
        }
      }
    });
  };

  const handleUpdateTransferConfig = async (updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/super-admin/transfer-config`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setToast({ type: 'success', message: 'Configuracion actualizada correctamente' });
      setShowTransferConfigModal(false);
      onReload();
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error al actualizar configuracion' });
    }
  };

  return (
    <div data-testid="transfers-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Sistema de Transferencias</h2>
        <div className="flex gap-3">
          {transferConfig && (
            <>
              <button onClick={() => handleToggleTransfers(!transferConfig.transfers_enabled)} className={`px-4 py-2 rounded-lg font-medium ${transferConfig.transfers_enabled ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}>
                {transferConfig.transfers_enabled ? 'Habilitadas' : 'Deshabilitadas'}
              </button>
              <button onClick={() => setShowTransferConfigModal(true)} className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#003594' }}>Configuracion</button>
              <button onClick={handleExecuteTransfersNow} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">Ejecutar Ahora</button>
            </>
          )}
        </div>
      </div>

      {transferConfig && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center gap-3 mb-2"><Clock className="w-6 h-6 text-blue-600" /><h3 className="font-semibold">Frecuencia</h3></div>
            <p className="text-2xl font-bold" style={{ color: '#003594' }}>{transferConfig.frequency_days} dias</p>
            <p className="text-xs text-gray-500 mt-1">Cada cuantos dias se ejecutan</p>
          </div>
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center gap-3 mb-2"><DollarSign className="w-6 h-6 text-green-600" /><h3 className="font-semibold">Monto Minimo</h3></div>
            <p className="text-2xl font-bold text-green-600">VES {(transferConfig.minimum_transfer_amount || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Para procesar transferencia</p>
          </div>
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center gap-3 mb-2"><Activity className="w-6 h-6 text-purple-600" /><h3 className="font-semibold">Estado</h3></div>
            <p className="text-lg font-bold">
              {transferConfig.maintenance_mode ? <span className="text-orange-600">Mantenimiento</span> : <span className="text-green-600">Operativo</span>}
            </p>
            {transferConfig.maintenance_message && <p className="text-xs text-gray-600 mt-1">{transferConfig.maintenance_message}</p>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b bg-gray-50"><h3 className="font-semibold">Historial de Transferencias</h3></div>
        {transfers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay transferencias registradas</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comercio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cuenta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {transfers.map((transfer) => (
                  <tr key={transfer.transfer_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono">#{transfer.transfer_id}</td>
                    <td className="px-6 py-4 text-sm"><p className="font-medium">{transfer.merchant_name}</p><p className="text-xs text-gray-500">{transfer.merchant_email}</p></td>
                    <td className="px-6 py-4 text-sm font-semibold">${transfer.amount.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm"><p className="font-medium">{transfer.bank_name}</p><p className="text-xs text-gray-500">{transfer.account_number}</p></td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${transfer.status === 'completed' ? 'bg-green-100 text-green-800' : transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : transfer.status === 'processing' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                        {transfer.status === 'completed' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                        {transfer.status === 'failed' && <XCircle className="w-3 h-3 inline mr-1" />}
                        {transfer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(transfer.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transfer Config Modal */}
      {showTransferConfigModal && transferConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b"><h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Configuracion de Transferencias</h2></div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center"><Database className="w-5 h-5 mr-2" />Cuentas Bancarias de la Plataforma</h3>
                <div className="mb-4 bg-white p-3 rounded border">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cuenta Pooling (Recepcion de Pagos)</label>
                  <input type="text" defaultValue={transferConfig.cauratech_pooling_account?.account_number || '01380010320100324207'} id="pooling_account_number" placeholder="Numero de cuenta (20 digitos)" className="w-full px-3 py-2 border rounded mb-2 text-sm font-mono" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" defaultValue={transferConfig.cauratech_pooling_account?.bank_code || '0138'} id="pooling_bank_code" placeholder="Codigo banco" className="px-3 py-2 border rounded text-sm" />
                    <input type="text" defaultValue={transferConfig.cauratech_pooling_account?.account_holder_id || 'J00408320126'} id="pooling_holder_id" placeholder="RIF titular" className="px-3 py-2 border rounded text-sm" />
                  </div>
                  <input type="text" defaultValue={transferConfig.cauratech_pooling_account?.account_holder_name || 'CAURA TECH C.A'} id="pooling_holder_name" placeholder="Nombre titular" className="w-full px-3 py-2 border rounded mt-2 text-sm" />
                </div>
                <div className="bg-white p-3 rounded border">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cuenta Comisiones (Para Recibir Comisiones)</label>
                  <input type="text" defaultValue={transferConfig.cauratech_commission_account?.account_number || '01380010330100324070'} id="commission_account_number" placeholder="Numero de cuenta (20 digitos)" className="w-full px-3 py-2 border rounded mb-2 text-sm font-mono" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" defaultValue={transferConfig.cauratech_commission_account?.bank_code || '0138'} id="commission_bank_code" placeholder="Codigo banco" className="px-3 py-2 border rounded text-sm" />
                    <input type="text" defaultValue={transferConfig.cauratech_commission_account?.account_holder_id || 'J00408320126'} id="commission_holder_id" placeholder="RIF titular" className="px-3 py-2 border rounded text-sm" />
                  </div>
                  <input type="text" defaultValue={transferConfig.cauratech_commission_account?.account_holder_name || 'CAURA TECH C.A'} id="commission_holder_name" placeholder="Nombre titular" className="w-full px-3 py-2 border rounded mt-2 text-sm" />
                </div>
                <p className="text-xs text-blue-700 mt-2">La cuenta pooling se usa para recibir pagos y enviar dispersiones. La cuenta de comisiones es donde se acumulan las comisiones de la plataforma.</p>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Configuracion de Transferencias</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frecuencia de Dispersion (dias)</label>
                  <input type="number" min="1" defaultValue={transferConfig.frequency_days || 7} id="frequency_days" className="w-full px-4 py-2 border rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">Cada cuantos dias se ejecutan las transferencias automaticas</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monto Minimo para Dispersion (VES)</label>
                  <input type="number" step="0.01" min="0" defaultValue={transferConfig.minimum_transfer_amount || 1000} id="minimum_transfer_amount" className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" defaultChecked={transferConfig.maintenance_mode} id="maintenance_mode" className="rounded" />
                    <span className="text-sm font-medium text-gray-700">Modo Mantenimiento</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje de Mantenimiento</label>
                  <textarea defaultValue={transferConfig.maintenance_message || ''} id="maintenance_message" className="w-full px-4 py-2 border rounded-lg" rows={2} />
                </div>
              </div>
            </div>
            <div className="p-6 border-t flex gap-3">
              <button onClick={() => setShowTransferConfigModal(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={() => {
                const updates = {
                  frequency_days: parseInt(document.getElementById('frequency_days').value),
                  minimum_transfer_amount: parseFloat(document.getElementById('minimum_transfer_amount').value),
                  maintenance_mode: document.getElementById('maintenance_mode').checked,
                  maintenance_message: document.getElementById('maintenance_message').value || null,
                  cauratech_pooling_account: { bank_code: document.getElementById('pooling_bank_code').value, bank_name: 'Banco Plaza', account_number: document.getElementById('pooling_account_number').value, account_type: 'corriente', account_holder_name: document.getElementById('pooling_holder_name').value, account_holder_id: document.getElementById('pooling_holder_id').value },
                  cauratech_commission_account: { bank_code: document.getElementById('commission_bank_code').value, bank_name: 'Banco Plaza', account_number: document.getElementById('commission_account_number').value, account_type: 'corriente', account_holder_name: document.getElementById('commission_holder_name').value, account_holder_id: document.getElementById('commission_holder_id').value }
                };
                handleUpdateTransferConfig(updates);
              }} className="flex-1 px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#003594' }}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog title={confirmDialog.title} message={confirmDialog.message} onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }} onCancel={() => setConfirmDialog(null)} confirmText={confirmDialog.confirmText} type={confirmDialog.type} />
      )}
    </div>
  );
}
